// src/components/rewards/DailyTrail.js
//
// THE DAILY TREASURE TRAIL (step 5). A 7-stop trail on a map — the same
// map art as Treasure Quest. Each day a student visits, their avatar walks
// one stop and they collect that day's coins (5, 5, 10, 10, 15, 20). Stop 7
// is a golden chest. Missing a day never resets the trail; the next visit
// simply takes the next step. After the chest, a new world begins (meadow,
// beach, snow, candy, jungle, space…).
//
// Props: onClose()

import ModalPortal from "../common/ModalPortal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import QuestMap from "../../games/treasureQuest/QuestMap";
import "../../games/treasureQuest/treasureQuest.css";
import CoinCounter, { Coin } from "../coins/CoinCounter";
import ChestOpener from "./ChestOpener";
import { useWallet } from "../../services/coinService";
import { useMyCloset } from "../../services/shopService";
import { useMyAvatar } from "../../services/avatarService";
import { claimDailyTrail, REWARDS, trailStatus } from "../../services/rewardsService";
import { trailCoins, useRewardSettings } from "../../services/rewardSettingsService";
import "./rewards.css";

const WALK_MS = 1400;
const WORLD_NAMES = { meadow: "Meadow", beach: "Sunny Beach", snow: "Snowy Peaks", candy: "Candy Land", jungle: "Jungle", space: "Outer Space" };

const msToMidnightNY = () => {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next - now;
};
const untilLabel = () => {
  const m = Math.ceil(msToMidnightNY() / 60000);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h} hour${h === 1 ? "" : "s"}` : `${m} minute${m === 1 ? "" : "s"}`;
};

const DailyTrail = ({ onClose }) => {
  const wallet = useWallet();
  const closet = useMyCloset();
  const me = useMyAvatar();
  const status = trailStatus(wallet.trail);
  const { settings } = useRewardSettings();
  const T = { ...REWARDS.trail, coins: trailCoins(settings) };

  const [at, setAt] = useState(null); // stop the avatar stands on (null = follow the wallet)
  const [walking, setWalking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [got, setGot] = useState(null); // { coins } | { chestId }
  const [chest, setChest] = useState(null);
  const [err, setErr] = useState("");
  const cardRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && !chest && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy, chest]);

  const world = T.worlds[status.world % T.worlds.length];
  const stand = at ?? status.stepsDone;
  const results = useMemo(() => Array.from({ length: Math.min(stand, 6) }, () => ({ won: true, stars: null })), [stand]);
  const labels = T.coins.map((_, i) => `${i + 1}`);
  const next = status.nextStep;
  const nextIsChest = next >= T.chestDay;

  const walk = async () => {
    setBusy(true);
    setErr("");
    try {
      const out = await claimDailyTrail();
      if (out?.already) {
        setBusy(false);
        return;
      }
      const target = out.trail.step;
      setAt(Math.max(0, target - 1));
      await new Promise((r) => setTimeout(r, 60));
      setWalking(true);
      setAt(target);
      await new Promise((r) => setTimeout(r, WALK_MS));
      setWalking(false);
      setGot(out);
      if (out.coins > 0) {
        const r = cardRef.current?.getBoundingClientRect();
        window.dispatchEvent(
          new CustomEvent("sbl:coins-earned", {
            detail: { amount: out.coins, coins: out.balance, reason: "daily", from: r ? { x: r.left + r.width / 2, y: r.top } : undefined },
          })
        );
      }
      if (out.chestId) setChest({ id: out.chestId, tier: "gold", label: "Day 7 of your treasure trail!" });
    } catch (e) {
      setErr(e?.message || "The trail is closed for a moment — try again soon.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalPortal>
      <div className="sbl-rw-pop" role="dialog" aria-modal="true" aria-label="Daily treasure trail" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose?.()}>
        <div className="sbl-trail">
          <header className="sbl-rw-head is-trail">
            <h2>🗺️ Daily Treasure Trail</h2>
            <span className="sbl-trail-world">{WORLD_NAMES[world] || world}</span>
            <CoinCounter />
            <button type="button" className="sbl-rw-x is-inline" onClick={onClose} aria-label="Close" disabled={busy}>
              ✕
            </button>
          </header>

          <div className="tq-root sbl-trail-map">
            <QuestMap
              theme={world}
              boards={6}
              at={stand}
              walking={walking}
              results={results}
              chestOpen={stand >= T.chestDay}
              look={me.look}
              avatarUrl={me.avatarUrl}
              outfit={closet.outfit}
              levelFrom={1}
              labels={labels}
            >
              {!walking && wallet.loaded && (
                <div className="tq-map-card sbl-trail-card" ref={cardRef}>
                  {got ? (
                    got.chestId ? (
                      <>
                        <h3>🎉 You reached the treasure!</h3>
                        <p>A golden chest is waiting for you.</p>
                        {!chest && (
                          <button type="button" className="tq-big-btn" onClick={() => setChest({ id: got.chestId, tier: "gold", label: "Day 7 of your treasure trail!" })}>
                            Open the chest 🎁
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <h3>
                          Day {got.trail.step}: +{got.coins} coins! <Coin size={22} />
                        </h3>
                        <p>Come back tomorrow to walk to day {got.trail.step + 1}{got.trail.step + 1 >= T.chestDay ? " — the treasure chest!" : "."}</p>
                      </>
                    )
                  ) : status.canClaim ? (
                    <>
                      <h3>{nextIsChest ? "Today: the golden chest! 🎁" : `Day ${next}: ${T.coins[next - 1]} coins`}</h3>
                      <p>{next === 1 ? "A new trail! Walk one stop every day you visit — the chest is at the end." : "Walk to your next stop!"}</p>
                      <button type="button" className="tq-big-btn" onClick={walk} disabled={busy}>
                        {busy ? "…" : nextIsChest ? "Walk to the treasure ➜" : `Walk to day ${next} ➜`}
                      </button>
                    </>
                  ) : (
                    <>
                      <h3>See you tomorrow! 👋</h3>
                      <p>
                        Your next step opens in {untilLabel()}.{" "}
                        {status.stepsDone >= T.chestDay ? "A brand-new world is next!" : `Day ${status.stepsDone + 1}${status.stepsDone + 1 >= T.chestDay ? " is the golden chest!" : ` pays ${T.coins[status.stepsDone]} coins.`}`}
                      </p>
                    </>
                  )}
                  {err && <p className="sbl-rw-err">{err}</p>}
                </div>
              )}
            </QuestMap>
          </div>

          <ol className="sbl-trail-days">
            {Array.from({ length: T.chestDay }, (_, i) => {
              const day = i + 1;
              const done = day <= stand;
              const isNext = !done && day === (got ? 0 : status.canClaim ? next : 0);
              return (
                <li key={day} className={`${done ? "is-done" : ""} ${isNext ? "is-next" : ""} ${day === T.chestDay ? "is-chest" : ""}`}>
                  <span className="sbl-trail-day">Day {day}</span>
                  <span className="sbl-trail-prize">
                    {day === T.chestDay ? (
                      "🎁"
                    ) : (
                      <>
                        <Coin size={16} /> {T.coins[i]}
                      </>
                    )}
                  </span>
                  {done && <span className="sbl-trail-check">✓</span>}
                </li>
              );
            })}
          </ol>
          <p className="sbl-trail-note">Missed a day? No problem — your trail waits for you.</p>
        </div>

        {chest && <ChestOpener chest={chest} onClose={() => setChest(null)} />}
      </div>
    </ModalPortal>
  );
};

export default DailyTrail;
